import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Dices, BatteryCharging, Radar } from "lucide-react";
import { Button } from "../ui/button";
import { navLinks, industries } from "../../data/mock";
import { GrafodataLogo } from "../brand/Logo";
import { cn } from "@/lib/utils";

const industryIcons = { Dices, BatteryCharging, Radar };

const accentDot = {
  igaming: "bg-[hsl(var(--igaming))]",
  battery: "bg-[hsl(var(--battery))]",
  automotive: "bg-[hsl(var(--automotive))]",
};

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/85 backdrop-blur-xl border-b border-border">
      <nav className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <GrafodataLogo />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.name === "Industries" ? (
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => setIndustriesOpen(true)}
                  onMouseLeave={() => setIndustriesOpen(false)}
                >
                  <Link
                    to={link.path}
                    className={cn(
                      "px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 flex items-center gap-1",
                      isActive(link.path)
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                    )}
                  >
                    {link.name}
                    <ChevronDown className="w-3.5 h-3.5" />
                  </Link>
                  {industriesOpen && (
                    <div className="absolute top-full left-0 pt-2 w-72">
                      <div className="rounded-2xl border border-border bg-popover shadow-xl shadow-black/30 p-2">
                        {industries.map((ind) => {
                          const Icon = industryIcons[ind.icon] || Dices;
                          return (
                            <Link
                              key={ind.slug}
                              to={`/industries/${ind.slug}`}
                              className="flex items-start gap-3 p-3 rounded-xl hover:bg-secondary transition-colors group/item"
                            >
                              <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0 group-hover/item:bg-background transition-colors">
                                <Icon className="w-4.5 h-4.5 text-foreground" />
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={cn("w-1.5 h-1.5 rounded-full", accentDot[ind.accent])} />
                                  <p className="text-sm font-medium text-foreground">{ind.fullName}</p>
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{ind.tagline}</p>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={cn(
                    "px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200",
                    isActive(link.path)
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  )}
                >
                  {link.name}
                </Link>
              )
            )}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/contact">
              <Button variant="ghost" className="text-muted-foreground hover:text-foreground hover:bg-secondary">
                Contact
              </Button>
            </Link>
            <Link to="/contact">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-5">
                Talk to an Architect
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-muted-foreground hover:text-foreground"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "px-4 py-3 text-sm font-medium rounded-lg transition-colors",
                    isActive(link.path)
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  )}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pl-4 pb-1 flex flex-col gap-1">
                {industries.map((ind) => (
                  <Link
                    key={ind.slug}
                    to={`/industries/${ind.slug}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-2 text-xs text-muted-foreground hover:text-foreground flex items-center gap-2"
                  >
                    <span className={cn("w-1.5 h-1.5 rounded-full", accentDot[ind.accent])} />
                    {ind.fullName}
                  </Link>
                ))}
              </div>
              <div className="pt-4 mt-2 border-t border-border">
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                    Talk to an Architect
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
